import React, { useContext, useState } from "react";
import Modal from "../generic/Modal";
import InputField from "../generic/InputField";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { UserContext } from "../../context/UserContext";
import { Messages } from "../../constants/Messages";
import SubmitButton from "../generic/SubmitButton";

interface LoginProps {
    onClose: () => void; // Function to close the modal
    onLogin: (email: string, password: string) => Promise<{ message: string, user: any, success: boolean }>; // Handle login logic outside
    onRegisterClick: () => void; // Navigate to register user flow
    onForgotPasswordClick: () => void; // Navigate to forgot password flow
}

const defaultMessages = {
    message: "",
};

const Login: React.FC<LoginProps> = ({
                                         onClose,
                                         onLogin,
                                         onRegisterClick,
                                         onForgotPasswordClick,
                                     }) => {
    const [ email, setEmail ] = useState<string>("");
    const [ password, setPassword ] = useState<string>("");
    const [ successMessage, setSuccessMessage ] = useState<{ message: string }>({ ...defaultMessages });
    const [ errorMessage, setErrorMessage ] = useState<{ title?: string, message: string }>({ ...defaultMessages });
    const { setIsLoggedIn } = useContext(UserContext);

    // Login logic inside the component
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Early return if required fields are missing
        if (!email || !password) {
            setSuccessMessage(defaultMessages); // Clear success messages
            setErrorMessage({
                message: Messages.ERROR_ALL_FIELDS_REQUIRED,
            });
            return;
        }

        const data = await onLogin(email, password);

        if (data.success) {
            setSuccessMessage(
                {
                    message: Messages.SUCCESS_LOGIN,
                },
            );
            setErrorMessage(defaultMessages); // Clear error messages
            setIsLoggedIn(true); // Set the user as logged in
            setTimeout(() => {
                onClose();
            }, 800);
        } else {
            setSuccessMessage(defaultMessages); // Clear success messages
            setErrorMessage({
                title: Messages.ERROR_LOGIN,
                message: data.message,
            });
        }
    };

    return (
        <Modal onClose={onClose}>
            <h2 className="text-lg font-semibold mb-2 text-center">Login</h2>

            <SharedAlertBanner successAlert={successMessage}
                               errorAlert={errorMessage}
                               onCloseError={() => setErrorMessage(defaultMessages)}
                               onCloseSuccess={() => setSuccessMessage(defaultMessages)}
            />

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <InputField
                    type="text"
                    value={email}
                    onChange={(value) => setEmail(value as string)}
                    label="Email / Username"
                    placeholder="Enter your email or username"
                    required
                    id="email"
                />
                <InputField
                    type="password"
                    value={password}
                    onChange={(value) => setPassword(value as string)}
                    label="Password"
                    placeholder="Enter your password"
                    required
                    id="password"
                />
                <SubmitButton>
                    Login
                </SubmitButton>
            </form>

            {/* Register button */}
            <div className="mt-6 text-sm text-center">
                <p>
                    Don’t have an account?{" "}
                    <a
                        onClick={onRegisterClick}
                        className="text-blue-600 hover:text-blue-800 underline transition duration-200"
                    >
                        Sign up here
                    </a>
                </p>
                <p className="mt-2">
                    Trouble accessing your account?{" "}
                    <a
                        onClick={onForgotPasswordClick}
                        className="text-blue-600 hover:text-blue-800 underline transition duration-200"
                    >
                        Forgot password?
                    </a>
                </p>
            </div>
        </Modal>
    );
};

export default Login;
